// Anna Text Chat Service
// =======================
// Provides text-based chat functionality with Anna for learning plan customization.
// Used in the PensumParser to help students refine their topics and subtopics.

import i18n from '../i18n';
import { ParsedTopic, ParsedSubtopic } from './pensumParser';
import { callClaudeRoute } from './claudeApi';

/**
 * Represents a single message in the chat conversation
 */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  /** Optional topic changes suggested in this message */
  suggestedChanges?: TopicChange[];
  /** Optional learning plan suggested in this message (Feature #79) */
  suggestedPlan?: SuggestedLearningPlan;
}

/**
 * A complete learning plan suggested by Anna (Feature #79)
 */
export interface SuggestedLearningPlan {
  topics: SuggestedPlanTopic[];
  summary: string;
  estimatedTotalHours: number;
}

/**
 * A topic within a suggested learning plan
 */
export interface SuggestedPlanTopic {
  title: string;
  description?: string;
  keyConcepts?: string[];
  estimatedDuration?: number; // minutes
  subtopics?: SuggestedPlanSubtopic[];
}

/**
 * A subtopic within a suggested plan topic
 */
export interface SuggestedPlanSubtopic {
  title: string;
  concepts?: string[];
}

/**
 * Represents a change Anna suggests to the topic structure
 */
export interface TopicChange {
  id: string;
  type: 'add_topic' | 'remove_topic' | 'edit_topic' | 'add_subtopic' | 'remove_subtopic' | 'edit_subtopic';
  /** The topic ID this change applies to (for subtopic changes) */
  topicId?: string;
  /** The subtopic ID this change applies to (for subtopic edits/removes) */
  subtopicId?: string;
  /** The new/updated data */
  data: {
    title?: string;
    concepts?: string[];
    description?: string;
  };
  /** Human-readable description of the change */
  description: string;
}

/**
 * Configuration options for the chat service
 */
interface AnnaTextChatConfig {
  topics: ParsedTopic[];
  pensumText: string;
  onMessageReceived?: (message: ChatMessage) => void;
  onError?: (error: Error) => void;
}

/**
 * Context for learning plan generation (Feature #79)
 */
export interface LearningPlanGenerationContext {
  journeyId: string;
  journeySubject: string;
  examDate?: string | Date;
  examFormat?: 'mundtlig' | 'skriftlig' | 'multiple_choice';
  existingTopics?: ParsedTopic[];
}


/**
 * System prompt for Anna in the text chat context
 */
const getAnnaTextChatPrompt = () => `${i18n.t('anna:textChat.systemPrompt')}\n\n${i18n.t('anna:textChat.changeInstructions')}`;

/**
 * Service for text-based chat with Anna about learning plan customization
 */
export class AnnaTextChatService {
  private topics: ParsedTopic[];
  private pensumText: string;
  private conversationHistory: ChatMessage[] = [];
  private config: AnnaTextChatConfig;
  /** Feature #167: Original pensum fingerprint for detecting duplicate uploads */
  private originalPensumFingerprint: string;

  constructor(config: AnnaTextChatConfig) {
    this.config = config;
    this.topics = config.topics;
    this.pensumText = config.pensumText;
    // Feature #167: Store fingerprint of original pensum text
    this.originalPensumFingerprint = this.createFingerprint(config.pensumText);
    console.log('[AnnaTextChat] Original pensum fingerprint stored:', this.originalPensumFingerprint.substring(0, 20) + '...');
  }

  /**
   * Feature #167: Create a fingerprint/hash of text content for comparison
   * This uses a simple approach: normalize the text, take key characters,
   * and create a reproducible string representation.
   */
  private createFingerprint(text: string): string {
    if (!text || text.trim().length === 0) {
      return '';
    }

    // Normalize: lowercase, remove extra whitespace, trim
    const normalized = text
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

    // If text is very short, just return it normalized
    if (normalized.length <= 100) {
      return normalized;
    }

    // For longer texts, create a fingerprint from:
    // 1. First 100 chars
    // 2. Middle 100 chars
    // 3. Last 100 chars
    // 4. Total length
    const first = normalized.substring(0, 100);
    const middle = normalized.substring(Math.floor(normalized.length / 2) - 50, Math.floor(normalized.length / 2) + 50);
    const last = normalized.substring(normalized.length - 100);
    const length = normalized.length;

    return `${first}|${middle}|${last}|${length}`;
  }

  /**
   * Feature #168: Check if material is genuinely new vs. existing pensum
   * Returns true if the material appears to be new content
   * Returns false if it's recognized as existing pensum material
   *
   * Comparison methods:
   * 1. Fingerprint match - if fingerprints are identical, it's the same material
   * 2. Significant overlap - if >70% of new text is found in existing pensum
   * 3. Exact prefix match - if new text starts with original pensum content
   */
  isNewMaterial(newText: string): boolean {
    if (!newText || newText.trim().length === 0) {
      console.log('[AnnaTextChat] isNewMaterial: Empty text provided, treating as not new');
      return false;
    }

    const newFingerprint = this.createFingerprint(newText);

    // Method 1: Fingerprint comparison
    if (newFingerprint === this.originalPensumFingerprint) {
      console.log('[AnnaTextChat] isNewMaterial: Fingerprints match exactly - material recognized as existing');
      return false;
    }

    // Method 2: Check if the new material is essentially a subset of existing pensum
    const normalizedNew = newText.toLowerCase().replace(/\s+/g, ' ').trim();
    const normalizedExisting = this.pensumText.toLowerCase().replace(/\s+/g, ' ').trim();

    // If either is empty, can't compare meaningfully
    if (!normalizedExisting || normalizedExisting.length < 50) {
      console.log('[AnnaTextChat] isNewMaterial: No significant existing pensum - treating as new material');
      return true;
    }

    // Method 3: Check for exact prefix match (common when re-uploading same file)
    const prefixLength = Math.min(300, normalizedNew.length, normalizedExisting.length);
    if (prefixLength > 50) {
      const newPrefix = normalizedNew.substring(0, prefixLength);
      const existingPrefix = normalizedExisting.substring(0, prefixLength);
      if (newPrefix === existingPrefix) {
        console.log('[AnnaTextChat] isNewMaterial: Prefix match detected - material recognized as existing');
        return false;
      }
    }

    // Method 4: Check for significant overlap using word matching
    // Extract significant words (>3 chars) from both texts
    const extractWords = (text: string): Set<string> => {
      return new Set(
        text
          .split(/\s+/)
          .filter(word => word.length > 3)
          .map(word => word.replace(/[^a-zæøå0-9]/gi, '').toLowerCase())
          .filter(word => word.length > 3)
      );
    };

    const newWords = extractWords(normalizedNew);
    const existingWords = extractWords(normalizedExisting);

    // Count how many words from new text exist in existing pensum
    let matchingWords = 0;
    newWords.forEach(word => {
      if (existingWords.has(word)) {
        matchingWords++;
      }
    });

    const overlapPercentage = newWords.size > 0 ? (matchingWords / newWords.size) * 100 : 0;
    console.log(`[AnnaTextChat] isNewMaterial: Word overlap ${overlapPercentage.toFixed(1)}% (${matchingWords}/${newWords.size} words)`);

    // If >70% of words in new text are found in existing pensum, it's likely the same material
    if (overlapPercentage > 70) {
      console.log('[AnnaTextChat] isNewMaterial: High word overlap - material recognized as existing');
      return false;
    }

    console.log('[AnnaTextChat] isNewMaterial: Material appears to be genuinely new');
    return true;
  }

  /**
   * Generate a unique ID for messages and changes
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Get the current topic structure as a formatted string for the AI context
   */
  private getTopicStructureString(): string {
    return this.topics.map((topic, idx) => {
      const subtopicsStr = topic.subtopics
        .map(sub => `    - ${sub.title}${sub.concepts?.length ? ` (${sub.concepts.join(', ')})` : ''}`)
        .join('\n');
      return `${idx + 1}. ${topic.title} (id: ${topic.id})${subtopicsStr ? '\n' + subtopicsStr : ''}`;
    }).join('\n\n');
  }

  /**
   * Build the conversation context for the AI
   */
  private buildPrompt(userMessage: string): string {
    const topicStructure = this.getTopicStructureString();

    // Build conversation history string (last 10 messages to avoid token limits)
    const recentHistory = this.conversationHistory.slice(-10);
    const historyStr = recentHistory
      .map(msg => `${msg.role === 'user' ? 'Studenten' : 'Anna'}: ${msg.content}`)
      .join('\n\n');

    return `${getAnnaTextChatPrompt()}

NUVÆRENDE EMNESTRUKTUR:
${topicStructure}

PENSUM KONTEKST (uddrag):
${this.pensumText.substring(0, 3000)}${this.pensumText.length > 3000 ? '...' : ''}

SAMTALE HIDTIL:
${historyStr || '(Ny samtale)'}

Studenten: ${userMessage}

Anna:`;
  }

  /**
   * Parse topic changes from Anna's response
   */
  private parseChangesFromResponse(response: string): TopicChange[] {
    const changes: TopicChange[] = [];

    // Look for <changes> tag in response (case-insensitive)
    const changesMatch = response.match(/<changes>([\s\S]*?)<\/changes>/i);
    if (!changesMatch) return changes;

    try {
      const changesJson = JSON.parse(changesMatch[1].trim());
      if (Array.isArray(changesJson)) {
        for (const change of changesJson) {
          if (change.type && change.description) {
            changes.push({
              id: this.generateId(),
              type: change.type,
              topicId: change.topicId,
              subtopicId: change.subtopicId,
              data: change.data || {},
              description: change.description
            });
          }
        }
      }
    } catch (error) {
      console.error('[AnnaTextChat] Failed to parse changes:', error);
    }

    return changes;
  }

  /**
   * Remove the <changes> tag from the response for display
   * Also cleans up intro text and normalizes whitespace
   */
  private cleanResponseForDisplay(response: string): string {
    // Step 1: Remove <changes> tags (case-insensitive, handles multiline)
    let cleaned = response.replace(/<changes>[\s\S]*?<\/changes>/gi, '');

    // Step 2: Remove common intro phrases that precede change suggestions
    const introPatterns = [
      /her er mit forslag:?\s*/gi,
      /her er mine forslag:?\s*/gi,
      /her er ændringen:?\s*/gi,
      /her er ændringerne:?\s*/gi,
      /jeg foreslår følgende:?\s*/gi,
      /jeg foreslår:?\s*/gi,
    ];
    for (const pattern of introPatterns) {
      cleaned = cleaned.replace(pattern, '');
    }

    // Step 3: Normalize multiple newlines (3+ becomes 2)
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');

    // Step 4: Trim whitespace
    cleaned = cleaned.trim();

    // Step 5: Fallback if content is empty but we had a response
    if (!cleaned && response.trim()) {
      cleaned = 'Jeg har et forslag til dig. Se detaljerne nedenfor.';
    }

    return cleaned;
  }

  /**
   * Send a message to Anna and get a response
   */
  async sendMessage(userMessage: string): Promise<ChatMessage> {
    // Add user message to history
    const userChatMessage: ChatMessage = {
      id: this.generateId(),
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    this.conversationHistory.push(userChatMessage);

    try {
      const prompt = this.buildPrompt(userMessage);

      const rawResponse = await callClaudeRoute('/api/journey-chat', { prompt, maxTokens: 8192 });

      // Debug: Log raw response to help diagnose issues
      console.log('[AnnaTextChat] Raw response:', rawResponse);
      console.log('[AnnaTextChat] Contains <changes> tag:', /<changes>/i.test(rawResponse));

      // Parse any suggested changes
      const suggestedChanges = this.parseChangesFromResponse(rawResponse);

      // Clean the response for display
      const displayContent = this.cleanResponseForDisplay(rawResponse);

      // Create assistant message
      const assistantMessage: ChatMessage = {
        id: this.generateId(),
        role: 'assistant',
        content: displayContent,
        timestamp: new Date(),
        suggestedChanges: suggestedChanges.length > 0 ? suggestedChanges : undefined
      };

      // Add to history
      this.conversationHistory.push(assistantMessage);

      // Notify callback if provided
      this.config.onMessageReceived?.(assistantMessage);

      return assistantMessage;

    } catch (error) {
      console.error('[AnnaTextChat] Error sending message:', error);
      this.config.onError?.(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  /**
   * Add new material to the context and notify Anna
   * Uses the isNewMaterial() method for sophisticated duplicate detection
   */
  async addMaterial(newText: string): Promise<ChatMessage> {
    // Use the sophisticated isNewMaterial check (Feature #169)
    // This checks fingerprints, prefix matches, and word overlap (>70% = existing)
    if (!this.isNewMaterial(newText)) {
      console.log('[AnnaTextChat] addMaterial: Material recognized as existing, returning guidance message');
      return {
        id: this.generateId(),
        role: 'assistant',
        content: `Jeg kan se dette materiale allerede er en del af din emneplan.

Er der noget specifikt ved planen du gerne vil ændre eller diskutere? Du kan også uploade *nyt* materiale hvis du vil udvide planen.`,
        timestamp: new Date()
      };
    }

    console.log('[AnnaTextChat] addMaterial: New material detected, processing...');

    // Continue with existing addMaterial logic for genuinely new material
    // Update the pensum text with the new material
    this.pensumText = this.pensumText + '\n\n--- TILFØJET MATERIALE ---\n' + newText;

    // Send a message to Anna about the new material
    return this.sendMessage(`Jeg har tilføjet nyt materiale til mit pensum. Kan du analysere det og foreslå hvordan det kan integreres i min emneplan?\n\nNyt materiale:\n${newText.substring(0, 1500)}${newText.length > 1500 ? '...' : ''}`);
  }

  /**
   * Update the topic list (call this after applying a change)
   */
  updateTopics(newTopics: ParsedTopic[]): void {
    this.topics = newTopics;
  }

  /**
   * Apply a TopicChange to the topic structure
   * Returns the updated topics array
   */
  applyChange(change: TopicChange, currentTopics: ParsedTopic[]): ParsedTopic[] {
    const topics = [...currentTopics];

    switch (change.type) {
      case 'add_topic': {
        const newTopic: ParsedTopic = {
          id: `topic-${this.generateId()}`,
          title: change.data.title || 'Nyt emne',
          subtopics: [],
          completedSessions: 0,
          totalEstimatedSessions: 3,
          description: change.data.description
        };
        topics.push(newTopic);
        break;
      }

      case 'remove_topic': {
        const topicIndex = topics.findIndex(t => t.id === change.topicId);
        if (topicIndex !== -1) {
          topics.splice(topicIndex, 1);
        }
        break;
      }

      case 'edit_topic': {
        const topic = topics.find(t => t.id === change.topicId);
        if (topic && change.data.title) {
          topic.title = change.data.title;
        }
        break;
      }

      case 'add_subtopic': {
        const topic = topics.find(t => t.id === change.topicId);
        if (topic) {
          const newSubtopic: ParsedSubtopic = {
            id: `sub-${this.generateId()}`,
            title: change.data.title || 'Nyt underemne',
            concepts: change.data.concepts || [],
            isCompleted: false
          };
          topic.subtopics.push(newSubtopic);
          // Update estimated sessions
          topic.totalEstimatedSessions = Math.max(3, Math.ceil(topic.subtopics.length * 1.5));
        }
        break;
      }

      case 'remove_subtopic': {
        const topic = topics.find(t => t.id === change.topicId);
        if (topic && change.subtopicId) {
          const subIndex = topic.subtopics.findIndex(s => s.id === change.subtopicId);
          if (subIndex !== -1) {
            topic.subtopics.splice(subIndex, 1);
          }
        }
        break;
      }

      case 'edit_subtopic': {
        const topic = topics.find(t => t.id === change.topicId);
        if (topic && change.subtopicId) {
          const subtopic = topic.subtopics.find(s => s.id === change.subtopicId);
          if (subtopic) {
            if (change.data.title) subtopic.title = change.data.title;
            if (change.data.concepts) subtopic.concepts = change.data.concepts;
          }
        }
        break;
      }
    }

    return topics;
  }

  /**
   * Get the conversation history
   */
  getHistory(): ChatMessage[] {
    return [...this.conversationHistory];
  }

  /**
   * Get a welcome message from Anna
   * Feature #170: Context-aware welcome message based on existing topics and pensum
   */
  getWelcomeMessage(): ChatMessage {
    const hasTopics = this.topics && this.topics.length > 0;
    const hasPensum = this.pensumText && this.pensumText.trim().length > 50;
    const topicCount = this.topics?.length || 0;
    const subtopicCount = this.topics?.reduce((sum, t) => sum + (t.subtopics?.length || 0), 0) || 0;

    let content: string;

    if (hasTopics && hasPensum) {
      // Context: User has both topics and pensum - they're editing/refining an existing plan
      console.log('[AnnaTextChat] getWelcomeMessage: Context-aware - existing plan with pensum');
      content = `Jeg kan se din emneplan med ${topicCount} emner og ${subtopicCount} underemner.

Hvad kan jeg hjælpe dig med? Du kan f.eks.:
• Tilføje eller fjerne emner fra planen
• Ændre underemner under et emne
• Diskutere hvordan emnerne hænger sammen
• Uploade *nyt* materiale for at udvide planen`;
    } else if (hasTopics && !hasPensum) {
      // Context: User has topics but no pensum - plan exists without source material
      console.log('[AnnaTextChat] getWelcomeMessage: Context-aware - topics without pensum');
      content = `Jeg kan se din emneplan med ${topicCount} emner.

Jeg bemærker at du ikke har tilknyttet materiale. Du kan enten:
• Fortsætte med at redigere emnerne direkte
• Uploade relevant materiale så jeg kan foreslå forbedringer
• Stille spørgsmål om de eksisterende emner`;
    } else if (!hasTopics && hasPensum) {
      // Context: User has pensum but no topics - ready to generate a plan
      console.log('[AnnaTextChat] getWelcomeMessage: Context-aware - pensum without topics');
      content = `Jeg har modtaget dit materiale og er klar til at hjælpe dig med at oprette en emneplan.

Skal jeg analysere materialet og foreslå en struktur med emner og underemner? Du kan også fortælle mig om der er bestemte områder du gerne vil fokusere på.`;
    } else {
      // Context: No topics, no pensum - fresh start
      console.log('[AnnaTextChat] getWelcomeMessage: Context-aware - fresh start');
      content = `Jeg er klar til at hjælpe dig med at oprette en emneplan.

Upload relevant materiale (PDF eller tekst), så analyserer jeg indholdet og foreslår en struktur. Du kan også beskrive hvad du gerne vil lære, hvis du ikke har materiale klar.`;
    }

    return {
      id: this.generateId(),
      role: 'assistant',
      content,
      timestamp: new Date()
    };
  }

  /**
   * Clear the conversation history
   */
  clearHistory(): void {
    this.conversationHistory = [];
  }
}

// ===== LEARNING PLAN GENERATION SERVICE (Feature #79) =====

/**
 * System prompt for Anna in learning plan generation mode
 */
const getAnnaLearningPlanPrompt = () => i18n.t('anna:learningPlan.systemPrompt');

/**
 * Format instructions for the <plan> JSON tag.
 * Shared by both LearningPlanGeneratorService and EditPlanGeneratorService.
 * This tells the LLM exactly what JSON structure to produce inside <plan> tags.
 */
const PLAN_FORMAT_INSTRUCTIONS = `
OUTPUT FORMAT:
You MUST include your suggested plan as valid JSON inside a <plan> tag. This is required for the app to parse it.

<plan>
{
  "summary": "Brief summary of the plan",
  "estimatedTotalHours": 10,
  "topics": [
    {
      "title": "Topic title",
      "description": "Brief description of what this topic covers",
      "keyConcepts": ["concept1", "concept2", "concept3"],
      "estimatedDuration": 60,
      "subtopics": [
        { "title": "Subtopic title", "concepts": ["concept1", "concept2"] }
      ]
    }
  ]
}
</plan>

RULES FOR THE <plan> TAG:
- estimatedDuration is in minutes
- estimatedTotalHours is the total across all topics
- Each topic must have at least 1 subtopic
- The JSON must be valid (no trailing commas, proper quoting)
- Write topic/subtopic titles in the student's language
- Include the <plan> tag AFTER your conversational response text
`;

/**
 * Service for generating learning plans from materials (Feature #79)
 */
export class LearningPlanGeneratorService {
  private context: LearningPlanGenerationContext;
  private conversationHistory: ChatMessage[] = [];

  constructor(context: LearningPlanGenerationContext) {
    this.context = context;
  }

  /**
   * Generate a unique ID for messages
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Build the initial context for plan generation
   * Feature #185: Include existing topics for integration when adding new material
   */
  private buildInitialPrompt(materialTexts: string[]): string {
    const combinedText = materialTexts.join('\n\n---\n\n');
    const examDateStr = this.context.examDate
      ? new Date(this.context.examDate).toLocaleDateString('da-DK')
      : 'Ikke angivet';

    const examFormatMap: Record<string, string> = {
      'mundtlig': 'Mundtlig eksamen',
      'skriftlig': 'Skriftlig eksamen',
      'multiple_choice': 'Multiple choice',
    };
    const examFormatStr = this.context.examFormat
      ? examFormatMap[this.context.examFormat] || 'Ikke angivet'
      : 'Ikke angivet';

    // Feature #185: Check if there are existing topics to integrate with
    const hasExistingTopics = this.context.existingTopics && this.context.existingTopics.length > 0;

    // Format existing topics if they exist
    let existingTopicsSection = '';
    let integrationInstruction = '';

    if (hasExistingTopics) {
      const topicsStr = this.context.existingTopics!.map((topic, idx) => {
        const subtopicsStr = topic.subtopics
          ?.map(sub => `    - ${sub.title}${sub.concepts?.length ? ` (${sub.concepts.join(', ')})` : ''}`)
          .join('\n') || '';
        return `${idx + 1}. ${topic.title}${subtopicsStr ? '\n' + subtopicsStr : ''}`;
      }).join('\n\n');

      existingTopicsSection = `
EKSISTERENDE EMNEPLAN:
${topicsStr}

`;
      integrationInstruction = `Studenten har allerede en eksisterende emneplan (se ovenfor) og har nu tilføjet NYT materiale.
Din opgave er at INTEGRERE det nye materiale med den eksisterende plan:
- Bevar de eksisterende emner og underemner
- Tilføj NYE emner/underemner baseret på det nye materiale
- Hvis det nye materiale uddyber eksisterende emner, udvid dem med flere underemner
- Den endelige plan skal inkludere BÅDE de gamle og de nye emner

`;
      console.log('[LearningPlanGenerator] Feature #185: Building prompt with existing topics for integration');
    }

    return `${getAnnaLearningPlanPrompt()}

KONTEKST:
Fag: ${this.context.journeySubject}
Eksamensdato: ${examDateStr}
Eksamensformat: ${examFormatStr}
${existingTopicsSection}
PENSUM-MATERIALE:
${combinedText.substring(0, 15000)}${combinedText.length > 15000 ? '\n\n... (materiale forkortet)' : ''}

---

${integrationInstruction}Opret venligst en struktureret emneplan baseret på materialet ovenfor.
${PLAN_FORMAT_INSTRUCTIONS}`;
  }

  /**
   * Build prompt for follow-up messages
   */
  private buildFollowUpPrompt(userMessage: string): string {
    // Include recent conversation history
    const recentHistory = this.conversationHistory.slice(-10);
    const historyStr = recentHistory
      .map(msg => `${msg.role === 'user' ? 'Studenten' : 'Anna'}: ${msg.content}`)
      .join('\n\n');

    return `${getAnnaLearningPlanPrompt()}

KONTEKST:
Fag: ${this.context.journeySubject}

SAMTALE HIDTIL:
${historyStr}

Studenten: ${userMessage}

If the student asks for changes to the plan or you suggest an updated plan, include the full updated plan in a <plan> tag.
${PLAN_FORMAT_INSTRUCTIONS}
Anna:`;
  }

  /**
   * Parse learning plan from response
   */
  private parsePlanFromResponse(response: string): SuggestedLearningPlan | undefined {
    const planMatch = response.match(/<plan>([\s\S]*?)<\/plan>/i);
    if (!planMatch) return undefined;

    try {
      const planJson = JSON.parse(planMatch[1].trim());
      return {
        summary: planJson.summary || '',
        estimatedTotalHours: planJson.estimatedTotalHours || 0,
        topics: (planJson.topics || []).map((t: SuggestedPlanTopic) => ({
          title: t.title,
          description: t.description,
          keyConcepts: t.keyConcepts || [],
          estimatedDuration: t.estimatedDuration,
          subtopics: (t.subtopics || []).map((s: SuggestedPlanSubtopic) => ({
            title: s.title,
            concepts: s.concepts || [],
          })),
        })),
      };
    } catch (error) {
      console.error('[LearningPlanGenerator] Failed to parse plan:', error);
      return undefined;
    }
  }

  /**
   * Clean response for display (remove <plan> tags)
   */
  private cleanResponseForDisplay(response: string): string {
    let cleaned = response.replace(/<plan>[\s\S]*?<\/plan>/gi, '');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    return cleaned.trim();
  }

  /**
   * Generate initial learning plan from materials
   */
  async generatePlanFromMaterials(materialTexts: string[]): Promise<ChatMessage> {
    try {
      const prompt = this.buildInitialPrompt(materialTexts);

      const rawResponse = await callClaudeRoute('/api/journey-chat', { prompt, maxTokens: 16384 });

      console.log('[LearningPlanGenerator] Raw response length:', rawResponse.length);
      console.log('[LearningPlanGenerator] Contains <plan> tag:', /<plan>/i.test(rawResponse));

      // Parse the learning plan
      const suggestedPlan = this.parsePlanFromResponse(rawResponse);

      // Create assistant message
      const assistantMessage: ChatMessage = {
        id: this.generateId(),
        role: 'assistant',
        content: this.cleanResponseForDisplay(rawResponse),
        timestamp: new Date(),
        suggestedPlan,
      };

      // Add to history
      this.conversationHistory.push(assistantMessage);

      return assistantMessage;

    } catch (error) {
      console.error('[LearningPlanGenerator] Error generating plan:', error);
      throw error;
    }
  }

  /**
   * Send a follow-up message (to refine the plan)
   */
  async sendMessage(userMessage: string): Promise<ChatMessage> {
    // Add user message to history
    const userChatMessage: ChatMessage = {
      id: this.generateId(),
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    this.conversationHistory.push(userChatMessage);

    try {
      const prompt = this.buildFollowUpPrompt(userMessage);

      const rawResponse = await callClaudeRoute('/api/journey-chat', { prompt, maxTokens: 16384 });

      // Check for updated plan
      const suggestedPlan = this.parsePlanFromResponse(rawResponse);

      // Create assistant message
      const assistantMessage: ChatMessage = {
        id: this.generateId(),
        role: 'assistant',
        content: this.cleanResponseForDisplay(rawResponse),
        timestamp: new Date(),
        suggestedPlan,
      };

      this.conversationHistory.push(assistantMessage);
      return assistantMessage;

    } catch (error) {
      console.error('[LearningPlanGenerator] Error sending message:', error);
      throw error;
    }
  }

  /**
   * Get conversation history
   */
  getHistory(): ChatMessage[] {
    return [...this.conversationHistory];
  }

  /**
   * Get welcome message
   */
  getWelcomeMessage(): ChatMessage {
    return {
      id: this.generateId(),
      role: 'assistant',
      content: `Hej! 👋 Lad os oprette en struktureret emneplan for ${this.context.journeySubject}.

Upload dine materialer (PDF, DOCX eller TXT), så analyserer jeg indholdet og foreslår en plan med emner, underemner og tidsestimater.

Har du allerede materialer klar?`,
      timestamp: new Date()
    };
  }

  /**
   * Clear history
   */
  clearHistory(): void {
    this.conversationHistory = [];
  }

  /**
   * Get the latest suggested plan from conversation
   */
  getLatestPlan(): SuggestedLearningPlan | undefined {
    // Find the most recent message with a plan
    for (let i = this.conversationHistory.length - 1; i >= 0; i--) {
      if (this.conversationHistory[i].suggestedPlan) {
        return this.conversationHistory[i].suggestedPlan;
      }
    }
    return undefined;
  }
}

export default AnnaTextChatService;

// ===== EDIT PLAN SERVICE (Feature #101) =====

/**
 * System prompt for Anna when editing existing learning plans
 */
const getAnnaEditPlanPrompt = () => i18n.t('anna:editPlan.systemPrompt');

/**
 * Extended context for editing existing learning plans (Feature #101)
 */
export interface EditPlanGenerationContext extends LearningPlanGenerationContext {
  /** The existing topics from the current plan */
  existingTopics: ParsedTopic[];
  /** Any newly uploaded materials to integrate */
  newMaterials?: { fileName: string; extractedText: string }[];
}

/**
 * Service for editing existing learning plans (Feature #101)
 * Uses a specialized prompt that understands edit context
 * Feature #176: Includes material tracking to detect duplicate uploads
 */
export class EditPlanGeneratorService {
  private context: EditPlanGenerationContext;
  private conversationHistory: ChatMessage[] = [];
  /** Feature #176: Fingerprints of existing materials for duplicate detection */
  private materialFingerprints: Set<string> = new Set();

  constructor(context: EditPlanGenerationContext) {
    this.context = context;
    // Feature #176: Store fingerprints of existing materials
    this.initializeMaterialFingerprints();
  }

  /**
   * Feature #176: Initialize fingerprints from existing materials
   */
  private initializeMaterialFingerprints(): void {
    if (this.context.newMaterials) {
      for (const material of this.context.newMaterials) {
        if (material.extractedText) {
          const fingerprint = this.createFingerprint(material.extractedText);
          this.materialFingerprints.add(fingerprint);
          console.log('[EditPlanGenerator] Stored fingerprint for:', material.fileName);
        }
      }
    }
    console.log('[EditPlanGenerator] Initialized with', this.materialFingerprints.size, 'material fingerprints');
  }

  /**
   * Feature #176: Create a fingerprint/hash of text content for comparison
   * Same algorithm as AnnaTextChatService for consistency
   */
  private createFingerprint(text: string): string {
    if (!text || text.trim().length === 0) {
      return '';
    }

    // Normalize: lowercase, remove extra whitespace, trim
    const normalized = text
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

    // If text is very short, just return it normalized
    if (normalized.length <= 100) {
      return normalized;
    }

    // For longer texts, create a fingerprint from:
    // 1. First 100 chars
    // 2. Middle 100 chars
    // 3. Last 100 chars
    // 4. Total length
    const first = normalized.substring(0, 100);
    const middle = normalized.substring(Math.floor(normalized.length / 2) - 50, Math.floor(normalized.length / 2) + 50);
    const last = normalized.substring(normalized.length - 100);
    const length = normalized.length;

    return `${first}|${middle}|${last}|${length}`;
  }

  /**
   * Feature #176: Check if material is genuinely new vs. existing
   * Returns true if the material appears to be new content
   * Returns false if it's recognized as existing material
   */
  isNewMaterial(newText: string): boolean {
    if (!newText || newText.trim().length === 0) {
      console.log('[EditPlanGenerator] isNewMaterial: Empty text provided, treating as not new');
      return false;
    }

    const newFingerprint = this.createFingerprint(newText);

    // Method 1: Check against stored fingerprints
    if (this.materialFingerprints.has(newFingerprint)) {
      console.log('[EditPlanGenerator] isNewMaterial: Fingerprint match - material recognized as existing');
      return false;
    }

    // Method 2: Check for prefix match against all existing materials
    const normalizedNew = newText.toLowerCase().replace(/\s+/g, ' ').trim();

    if (this.context.newMaterials) {
      for (const material of this.context.newMaterials) {
        if (!material.extractedText) continue;

        const normalizedExisting = material.extractedText.toLowerCase().replace(/\s+/g, ' ').trim();

        // Check prefix match (first 300 chars)
        const prefixLength = Math.min(300, normalizedNew.length, normalizedExisting.length);
        if (prefixLength > 50) {
          const newPrefix = normalizedNew.substring(0, prefixLength);
          const existingPrefix = normalizedExisting.substring(0, prefixLength);
          if (newPrefix === existingPrefix) {
            console.log('[EditPlanGenerator] isNewMaterial: Prefix match detected - material recognized as existing');
            return false;
          }
        }

        // Check word overlap (>70% = existing)
        const extractWords = (text: string): Set<string> => {
          return new Set(
            text
              .split(/\s+/)
              .filter(word => word.length > 3)
              .map(word => word.replace(/[^a-zæøå0-9]/gi, '').toLowerCase())
              .filter(word => word.length > 3)
          );
        };

        const newWords = extractWords(normalizedNew);
        const existingWords = extractWords(normalizedExisting);

        let matchingWords = 0;
        newWords.forEach(word => {
          if (existingWords.has(word)) {
            matchingWords++;
          }
        });

        const overlapPercentage = newWords.size > 0 ? (matchingWords / newWords.size) * 100 : 0;

        if (overlapPercentage > 70) {
          console.log(`[EditPlanGenerator] isNewMaterial: High word overlap ${overlapPercentage.toFixed(1)}% with ${material.fileName} - material recognized as existing`);
          return false;
        }
      }
    }

    console.log('[EditPlanGenerator] isNewMaterial: Material appears to be genuinely new');
    return true;
  }

  /**
   * Feature #176: Add new material with duplicate detection
   * Returns a guidance message if material is recognized as existing
   * Otherwise processes the material for integration
   */
  async addMaterial(newText: string, fileName?: string): Promise<ChatMessage> {
    // Check if this is genuinely new material
    if (!this.isNewMaterial(newText)) {
      console.log('[EditPlanGenerator] addMaterial: Material recognized as existing, returning guidance message');
      return {
        id: this.generateId(),
        role: 'assistant',
        content: `Jeg kan se dette materiale allerede er en del af din emneplan.

Er der noget specifikt ved planen du gerne vil ændre eller diskutere? Du kan også uploade *nyt* materiale hvis du vil udvide planen.`,
        timestamp: new Date()
      };
    }

    console.log('[EditPlanGenerator] addMaterial: New material detected, processing...');

    // Store the fingerprint for future duplicate detection
    const fingerprint = this.createFingerprint(newText);
    this.materialFingerprints.add(fingerprint);

    // Add to context for future prompts
    if (!this.context.newMaterials) {
      this.context.newMaterials = [];
    }
    this.context.newMaterials.push({
      fileName: fileName || 'Nyt materiale',
      extractedText: newText
    });

    // Send a message to process the new material
    return this.sendMessage(`Jeg har tilføjet nyt materiale. Kan du analysere det og foreslå hvordan det kan integreres i min eksisterende emneplan?\n\nNyt materiale:\n${newText.substring(0, 1500)}${newText.length > 1500 ? '...' : ''}`);
  }

  /**
   * Generate a unique ID for messages
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Format existing topics as context string
   */
  private formatExistingTopics(): string {
    return this.context.existingTopics.map((topic, idx) => {
      const subtopicsStr = topic.subtopics
        .map(sub => `    - ${sub.title}${sub.isCompleted ? ' ✓' : ''}`)
        .join('\n');
      return `${idx + 1}. ${topic.title}\n${subtopicsStr}`;
    }).join('\n\n');
  }

  /**
   * Build prompt for edit requests
   */
  private buildEditPrompt(userMessage: string): string {
    const existingTopicsStr = this.formatExistingTopics();
    const newMaterialsStr = this.context.newMaterials
      ?.map(m => `--- ${m.fileName} ---\n${m.extractedText.substring(0, 2000)}`)
      .join('\n\n') || '';

    // Include recent conversation history
    const recentHistory = this.conversationHistory.slice(-10);
    const historyStr = recentHistory
      .map(msg => `${msg.role === 'user' ? 'Studenten' : 'Anna'}: ${msg.content}`)
      .join('\n\n');

    return `${getAnnaEditPlanPrompt()}

FAG: ${this.context.journeySubject}

NUVÆRENDE EMNEPLAN:
${existingTopicsStr}

${newMaterialsStr ? `NYT MATERIALE TIL INTEGRATION:\n${newMaterialsStr}` : ''}

SAMTALE HIDTIL:
${historyStr || '(Ny samtale)'}

Studenten: ${userMessage}

If the student asks for changes to the plan or you suggest an updated plan, include the full updated plan in a <plan> tag.
${PLAN_FORMAT_INSTRUCTIONS}
Anna:`;
  }

  /**
   * Parse learning plan from response (same as LearningPlanGeneratorService)
   */
  private parsePlanFromResponse(response: string): SuggestedLearningPlan | undefined {
    const planMatch = response.match(/<plan>([\s\S]*?)<\/plan>/i);
    if (!planMatch) return undefined;

    try {
      const planJson = JSON.parse(planMatch[1].trim());
      return {
        summary: planJson.summary || '',
        estimatedTotalHours: planJson.estimatedTotalHours || 0,
        topics: (planJson.topics || []).map((t: SuggestedPlanTopic) => ({
          title: t.title,
          description: t.description,
          keyConcepts: t.keyConcepts || [],
          estimatedDuration: t.estimatedDuration,
          subtopics: (t.subtopics || []).map((s: SuggestedPlanSubtopic) => ({
            title: s.title,
            concepts: s.concepts || [],
          })),
        })),
      };
    } catch (error) {
      console.error('[EditPlanGenerator] Failed to parse plan:', error);
      return undefined;
    }
  }

  /**
   * Clean response for display
   */
  private cleanResponseForDisplay(response: string): string {
    let cleaned = response.replace(/<plan>[\s\S]*?<\/plan>/gi, '');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    return cleaned.trim();
  }

  /**
   * Send a message to get plan edit suggestions
   */
  async sendMessage(userMessage: string): Promise<ChatMessage> {
    // Add user message to history
    const userChatMessage: ChatMessage = {
      id: this.generateId(),
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    this.conversationHistory.push(userChatMessage);

    try {
      const prompt = this.buildEditPrompt(userMessage);

      const rawResponse = await callClaudeRoute('/api/journey-chat', { prompt, maxTokens: 16384 });

      // Check for updated plan
      const suggestedPlan = this.parsePlanFromResponse(rawResponse);

      // Create assistant message
      const assistantMessage: ChatMessage = {
        id: this.generateId(),
        role: 'assistant',
        content: this.cleanResponseForDisplay(rawResponse),
        timestamp: new Date(),
        suggestedPlan,
      };

      this.conversationHistory.push(assistantMessage);
      return assistantMessage;

    } catch (error) {
      console.error('[EditPlanGenerator] Error sending message:', error);
      throw error;
    }
  }

  /**
   * Generate an updated plan from existing topics and optional new materials
   * Feature #101: Enables plan generation in edit context
   */
  async generatePlanFromMaterials(materialTexts: string[]): Promise<ChatMessage> {
    try {
      // Build prompt that includes existing topics and new materials
      const existingTopicsStr = this.formatExistingTopics();
      const newMaterialsStr = materialTexts.join('\n\n---\n\n');

      const prompt = `${getAnnaEditPlanPrompt()}

FAG: ${this.context.journeySubject}

NUVÆRENDE EMNEPLAN:
${existingTopicsStr}

NYT MATERIALE TIL INTEGRATION:
${newMaterialsStr.substring(0, 15000)}${newMaterialsStr.length > 15000 ? '\n\n... (materiale forkortet)' : ''}

---

Opret venligst en OPDATERET emneplan der integrerer det nye materiale med den eksisterende plan. Bevar gerne strukturen hvor det giver mening, men tilføj nye emner/underemner baseret på det nye materiale.
${PLAN_FORMAT_INSTRUCTIONS}`;

      const rawResponse = await callClaudeRoute('/api/journey-chat', { prompt, maxTokens: 16384 });

      console.log('[EditPlanGenerator] Raw response length:', rawResponse.length);
      console.log('[EditPlanGenerator] Contains <plan> tag:', /<plan>/i.test(rawResponse));

      // Parse the learning plan
      const suggestedPlan = this.parsePlanFromResponse(rawResponse);

      // Create assistant message
      const assistantMessage: ChatMessage = {
        id: this.generateId(),
        role: 'assistant',
        content: this.cleanResponseForDisplay(rawResponse),
        timestamp: new Date(),
        suggestedPlan,
      };

      // Add to history
      this.conversationHistory.push(assistantMessage);

      return assistantMessage;

    } catch (error) {
      console.error('[EditPlanGenerator] Error generating plan:', error);
      throw error;
    }
  }

  /**
   * Get conversation history
   */
  getHistory(): ChatMessage[] {
    return [...this.conversationHistory];
  }

  /**
   * Get edit-specific welcome message (Feature #101)
   * Unlike the dashboard intro, this directly addresses the edit context
   */
  getWelcomeMessage(): ChatMessage {
    const topicCount = this.context.existingTopics.length;
    const hasNewMaterials = this.context.newMaterials && this.context.newMaterials.length > 0;

    let content = `Jeg kan se din eksisterende emneplan for ${this.context.journeySubject} med ${topicCount} emner.`;

    if (hasNewMaterials) {
      content += `\n\nDu har også uploadet nyt materiale. Fortæl mig, hvordan du vil integrere det - skal jeg foreslå nye emner, eller tilføje til eksisterende?`;
    } else {
      content += `\n\nHvad vil du gerne ændre? Du kan f.eks.:
• Tilføje eller fjerne emner
• Ændre underemner
• Justere fokusområder`;
    }

    return {
      id: this.generateId(),
      role: 'assistant',
      content,
      timestamp: new Date()
    };
  }

  /**
   * Clear history
   */
  clearHistory(): void {
    this.conversationHistory = [];
  }

  /**
   * Get the latest suggested plan from conversation
   */
  getLatestPlan(): SuggestedLearningPlan | undefined {
    for (let i = this.conversationHistory.length - 1; i >= 0; i--) {
      if (this.conversationHistory[i].suggestedPlan) {
        return this.conversationHistory[i].suggestedPlan;
      }
    }
    return undefined;
  }
}
